import { useContext, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Label from '../../../components/form/Label'
import catchError from '../../../components/global/CatchError'
import { DropZoneCustom } from '../../../components/global/DropZoneBVA'
import FlexColumn from '../../../components/global/FlexColumn'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import { StyledButtonLink } from '../../../components/table/Td'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { serverFile } from '../../../services/file.service'
import { urlRegex } from '../../../utils/global-function'
import { StyledButton } from '../Login'
import { AdminContainer, TitleWrapper } from '../users/AdminUsers'
import { AdminForm } from '../users/AdminUsersForm'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

const AdminPartnersForm = () => {
    const user = useContext(UserContext)
    const location = useLocation()
    const params = useParams()
    const { t } = useTranslation()

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const [currentLang, setCurrentLang] = useState(lng)

    const navigate = useNavigate()
    const BACK_LINK = `/${lng}/bva-admin/partenaires`

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [logo, setLogo] = useState<File[]>([])
    const [currentLogo, setCurrentLogo] = useState<string | undefined>('')
    const [logoUrl, setLogoUrl] = useState('')
    const [logoMode, setLogoMode] = useState<'upload' | 'url'>('upload')
    const [link, setLink] = useState('')
    const [order, setOrder] = useState(0)

    const [isLoading, setIsLoading] = useState(true)

    const isAddForm = location.pathname.includes('/bva-admin/partenaires/creer')

    useEffect(() => {
        verifyToken()
        if ((!isAddForm && isLoading) || currentLang !== lng) {
            setCurrentLang(lng)
            apiClient
                .post(`/partners/one/${params.id}`, { lng })
                .then((res) => {
                    setName(res.data.name)
                    setDescription(res.data.description || '')

                    if (res.data.logo && res.data.logo.startsWith('http')) {
                        setLogoUrl(res.data.logo)
                        setLogoMode('url')
                        setCurrentLogo(undefined)
                    } else {
                        setCurrentLogo(serverFile(res.data.logo))
                        setLogoUrl('')
                        setLogoMode('upload')
                    }

                    setLink(res.data.link || '')
                    setOrder(res.data.order || 0)
                    setIsLoading(false)
                })
                .catch((err) => {
                    catchError(err.response.data)
                })
        } else {
            setIsLoading(false)
        }
    }, [isAddForm, isLoading, params.id, lng])

    if (isLoading || currentLang !== lng) return <Loader />

    const isNameValidate = name.length > 2
    const isLinkValidate = !link || (link.length > 0 && urlRegex.test(link))
    const isLogoUrlValidate = logoMode === 'url' ? !logoUrl || (logoUrl.length > 0 && urlRegex.test(logoUrl)) : true
    const isLogoValid =
        logoMode === 'upload' ? (isAddForm ? logo.length > 0 : true) : isAddForm ? logoUrl.length > 0 : true

    const isValidate = isNameValidate && isLinkValidate && isLogoUrlValidate && isLogoValid

    const submit = () => {
        verifyToken()
        const formData = new FormData()
        formData.append('name', name)
        formData.append('description', description)

        if (logoMode === 'url') {
            if (logoUrl) {
                formData.append('logo', logoUrl)
            }
        } else {
            if (logo.length > 0) {
                formData.append('logo', logo[0])
            }
        }

        formData.append('link', link)
        formData.append('order', order.toString())

        if (isAddForm) {
            apiClient
                .post('/partners', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(() =>
                    sweetAlertSuccess(`Le partenaire a bien été créé.`).then(() => {
                        navigate(BACK_LINK)
                    }),
                )
                .catch((err) => {
                    catchError(err.response.data)
                })
        } else {
            formData.append('lng', lng)

            apiClient
                .patch(`/partners/${params.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then(() =>
                    sweetAlertSuccess(`Le partenaire a bien été modifié.`).then(() => {
                        navigate(BACK_LINK)
                    }),
                )
                .catch((err) => {
                    catchError(err.response.data)
                })
        }
    }

    return (
        <>
            <TitleWrapper>
                <Title>{isAddForm ? 'Créer' : 'Modifier'} un partenaire</Title>
                <StyledButtonLink to={BACK_LINK} className="btn btn-primary mb-2">
                    Retour
                </StyledButtonLink>
            </TitleWrapper>
            <AdminContainer>
                <AdminForm>
                    <Form.Group className="mb-3">
                        <Label>Nom du partenaire *</Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom du partenaire"
                            onChange={(e) => setName(e.target.value)}
                            value={name}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Description</Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Description"
                            onChange={(e) => setDescription(e.target.value)}
                            value={description}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Logo</Label>
                        <LogoModeSelector>
                            <ModeButton
                                type="button"
                                active={logoMode === 'upload'}
                                onClick={() => setLogoMode('upload')}
                            >
                                Upload
                            </ModeButton>
                            <ModeButton type="button" active={logoMode === 'url'} onClick={() => setLogoMode('url')}>
                                URL externe
                            </ModeButton>
                        </LogoModeSelector>
                        <ImageWrapper>
                            {logoMode === 'upload' ? (
                                <DropZoneCustom setImage={setLogo} image={logo} currentImage={currentLogo} />
                            ) : (
                                <UrlInputWrapper>
                                    <Form.Control
                                        type="text"
                                        placeholder="https://example.com/logo.png"
                                        onChange={(e) => setLogoUrl(e.target.value)}
                                        value={logoUrl}
                                        isInvalid={logoUrl.length > 0 && !urlRegex.test(logoUrl)}
                                    />
                                    {logoUrl && urlRegex.test(logoUrl) && (
                                        <PreviewImage
                                            src={logoUrl}
                                            alt="Prévisualisation du logo"
                                            onError={(e) => {
                                                e.currentTarget.src = ''
                                            }}
                                        />
                                    )}
                                </UrlInputWrapper>
                            )}
                        </ImageWrapper>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Lien</Label>
                        <Form.Control
                            type="text"
                            placeholder="Lien vers le site du partenaire"
                            onChange={(e) => setLink(e.target.value)}
                            value={link}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Label>Ordre d'affichage</Label>
                        <Form.Control
                            type="number"
                            placeholder="0"
                            onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                            value={order}
                        />
                    </Form.Group>
                    <StyledButton variant="primary" type="button" disabled={!isValidate} onClick={submit}>
                        {isAddForm ? 'Créer' : 'Modifier'}
                    </StyledButton>
                </AdminForm>
            </AdminContainer>
        </>
    )
}

const ImageWrapper = styled.div`
    display: flex;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`

const LogoModeSelector = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
`

const ModeButton = styled.button<{ active: boolean }>`
    padding: 8px 16px;
    border: 1px solid #ccc;
    background: ${(props) => (props.active ? 'var(--primary-color, #B40000)' : '#fff')};
    color: ${(props) => (props.active ? '#fff' : '#333')};
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${(props) => (props.active ? 'var(--primary-color, #B40000)' : '#f0f0f0')};
    }
`

const UrlInputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const PreviewImage = styled.img`
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 5px;
`

export default AdminPartnersForm
